import axios from 'api';

/**
 * Logs frontend events to the backend for debugging
 * Use case: Track user actions that don't reach the server (save clicks, errors, etc.)
 * @param {string} event - Event name (e.g., 'SAVE_CLICKED', 'SAVE_SUCCESS', 'SAVE_ERROR')
 * @param {string} token - Customer token
 * @param {Object} data - Additional data to log
 */
export const logFrontendEvent = async (event, token, data = {}) => {
    try {
        await axios.post('/frontend-log', {
            event,
            token,
            data,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
        });
    } catch (e) {
        // Silent fail - logging should never break the app
        console.warn('Frontend logging failed:', e);
    }
};

/**
 * Fetches and organizes customer meal plan data
 * Use case: Initial meal plan loading when customer accesses their personalized meal plan page
 * @param {string} token - Customer's unique token for accessing their meal plan
 * @returns {Object} Object containing customerDetails, MealsByDate by delivery date, and sortedDates
 */
export const fetchMealPlan = async (token) => {
    const response = await axios.get(`/orders/${token}`);
    // Group orders by delivery date
    const MealsByDate = {};

    response.data.orders.forEach(order => {
        const deliveryDate = order.deliveryDate || 'No Date';
        if (!MealsByDate[deliveryDate]) {
            MealsByDate[deliveryDate] = [];
        }
        MealsByDate[deliveryDate].push(order);
    });

    // Sort orders within each delivery date by quantity (highest first)
    Object.keys(MealsByDate).forEach(deliveryDate => {
        MealsByDate[deliveryDate].sort((a, b) => b.quantity - a.quantity);
    });

    // Sort delivery dates
    delete response.data.orders;
    return {
        customerDetails: response.data,
        MealsByDate,
    };
};

// Next 6 weeks per delivery day: { schedule: [{ deliveryDay, nextDeliveryDate, dates: [{ date, locked, skipped }] }] }
export const getSkips = async (token) => {
    const response = await axios.get(`/orders/${token}/skips`);
    return response.data;
};

export const updateSkips = async (token, { skip = [], unskip = [] }) => {
    return await axios.put(`/orders/${token}/skips`, { skip, unskip });
};

export const skipEmptyDates = async (token, dates) => (await axios.post(`/orders/${token}/skip-empty-dates`, { dates })).data;

/**
 * Batch saves all pending changes for a delivery date
 * Use case: When user clicks "Save Changes" or completes payment/refund for a date
 * @param {string} token - Customer's unique token
 * @param {string} deliveryDate - The delivery date for these changes
 * @param {Array} changes - Array of change objects to process
 * @param {Object} billingChanges - Object containing billing details for the delivery date
 * @returns {Object} API response with results for each change
 */
export const batchSave = async (token, deliveryDate, changes, billingChanges) => {
    return await axios.post(`/orders/${token}/batch-save`, {
        deliveryDate,
        changes,
        billingChanges
    });
};

/**
 * Stores pending changes server-side before Stripe checkout redirect
 * @param {string} token - Customer's unique token
 * @param {Object} changesPerDate - { [deliveryDate]: { changes: [...] } }
 * @param {Object} billingChanges - Billing changes object
 * @returns {Object} { success, pendingId }
 */
export const storePendingChanges = async (token, changesPerDate, billingChanges) => {
    const response = await axios.post('/pending-changes', {
        token,
        changesPerDate,
        billingChanges
    });
    return response.data;
};

/**
 * Creates a Stripe checkout session for payment
 * @param {string} pendingId - ID from storePendingChanges
 * @param {Array} lineItems - [{ deliveryDate, amount, description }]
 * @param {string} token - Customer's unique token
 * @returns {Object} { success, url, sessionId }
 */
export const createCheckoutSession = async (pendingId, lineItems, token) => {
    const response = await axios.post('/create-checkout-session', {
        pendingId,
        lineItems,
        token
    });
    return response.data;
};

/**
 * Checks if pending changes have been processed after Stripe payment
 * @param {string} pendingId - ID from storePendingChanges
 * @returns {Object} { success, processed }
 */
export const checkPendingStatus = async (pendingId) => {
    const response = await axios.get(`/pending-changes/${pendingId}/status`);
    return response.data;
};

/**
 * Fetches prior payment totals grouped by delivery date
 * @param {string} token - Customer's unique token
 * @returns {Object} { success, paymentsByDate: { [date]: totalPaid } }
 */
export const getPaymentHistory = async (token) => {
    const response = await axios.get(`/payment-history/${token}`);
    return response.data;
};

/**
 * Looks up the sales tax rate for a zip code
 * @param {string} zip - Delivery zip code
 * @returns {Object} { success, supported, taxRate } or { success, supported: false, message }
 */
export const fetchTaxRate = async (zip) => {
    const response = await axios.get(`/tax-rate/${encodeURIComponent(zip)}`);
    return response.data;
};

// { rate, existingFeeStart } — the backend's EXISTING_FEE_START / CARD_FEE_RATE env values
export const fetchCardFeeConfig = async () => {
    const response = await axios.get('/card-fee');
    return response.data;
};

